import { normalizeMaterialCode, sparePartCodeKey } from './StringHelper'

/**
 * The possible outcomes of adding a spare part that the catalog flagged as discontinued.
 */
export const SubstitutionOutcome = {
	/** The code the user picked is what got registered: nothing was substituted. */
	NONE: 'NONE',
	/** The picked code was discontinued and its substitute went in instead. */
	SUBSTITUTED: 'SUBSTITUTED',
	/** The material that applies was already in the request, so nothing was added. */
	ALREADY_PRESENT: 'ALREADY_PRESENT',
	/** The server did not report the outcome and it could not be established. */
	UNDETERMINED: 'UNDETERMINED'
}

/**
 * `code | description`, or just the code when there is no description, or empty when there is no
 * material at all. Keeps every message naming a material the same way.
 * @param {*} material
 */
const buildMaterialLabel = (material) => {
	if (!material || !material.Code) return ''
	return material.Description ? `${material.Code} | ${material.Description}` : `${material.Code}`
}

/**
 * Read the substitution report the add operation returned.
 *
 * The server states what it did — the requested code, the code and description that actually went
 * in, and whether it substituted or found the material already present — so the portal does not
 * have to work it out. Returns null when the response carries no report, which is what tells the
 * caller to fall back.
 *
 * @param {*} resultingRequest the request returned by the add operation
 */
export const readSubstitutionReport = (resultingRequest) => {
	if (!resultingRequest || !resultingRequest.Substitution) return null

	let report = resultingRequest.Substitution
	// A report that states neither flag is not a report — a partially serialized one, or an
	// unrelated property that happens to share the name. Treating it as authoritative would
	// silence a real substitution, so it falls through to the comparison instead.
	if (typeof report.WasSubstituted !== 'boolean' && typeof report.WasAlreadyPresent !== 'boolean') return null

	return report
}

/**
 * The material that actually went into the request, as `{ Code, Description }`.
 *
 * Taken from the server's report when it is there: the catalog can have changed between the moment
 * the listing loaded and the moment the user clicked, and what the server applied is the truth.
 * Falls back to the substitute the listing anticipated so the message still names something.
 *
 * @param {*} resultingRequest the request returned by the add operation
 * @param {*} anticipatedSubstitute the substitute the catalog predicted, or null
 */
export const getAppliedMaterial = (resultingRequest, anticipatedSubstitute) => {
	let report = readSubstitutionReport(resultingRequest)
	if (report && report.AppliedCode) {
		return { Code: report.AppliedCode, Description: report.AppliedDescription }
	}
	return anticipatedSubstitute ? anticipatedSubstitute : null
}

/**
 * Establish the outcome by comparing the code that was sent against the rows that came back.
 *
 * Legacy path, reached only when the server reports no outcome. Restricted to the equipment the
 * part was added to: a request can span several equipments, and the same code sitting on a
 * different equipment says nothing about what happened here.
 */
const resolveByComparison = (requestedCode, anticipatedSubstitute, resultingRequest, serie) => {
	let requestedKey = normalizeMaterialCode(requestedCode)
	if (requestedKey.length === 0) return SubstitutionOutcome.UNDETERMINED
	if (!resultingRequest || !resultingRequest.SpareParts) return SubstitutionOutcome.UNDETERMINED

	let presentKeys = resultingRequest.SpareParts.filter(
		(sparePart) => !serie || (sparePart && sparePart.Equipment && sparePart.Equipment.Serie === serie)
	).map((sparePart) => normalizeMaterialCode(sparePart ? sparePart.Code : ''))

	if (presentKeys.indexOf(requestedKey) !== -1) return SubstitutionOutcome.NONE

	let substituteKey = anticipatedSubstitute ? normalizeMaterialCode(anticipatedSubstitute.Code) : ''
	if (substituteKey.length > 0 && presentKeys.indexOf(substituteKey) !== -1) return SubstitutionOutcome.SUBSTITUTED

	return SubstitutionOutcome.UNDETERMINED
}

/**
 * Determine what the server actually did with the spare part that was just added.
 *
 * Reads the outcome the server reported. That is what keeps the message honest when the catalog
 * changed between the moment the listing was loaded and the moment the user hit add — including
 * the case where the listing showed no warning at all and the server substituted anyway, which no
 * comparison against an anticipated substitute could have caught.
 *
 * When the response carries no report, it falls back to comparing the code that was sent against
 * the rows that came back, scoped to the equipment.
 *
 * @param {*} requestedCode the spare part code the user picked
 * @param {*} anticipatedSubstitute the substitute the catalog predicted, or null
 * @param {*} resultingRequest the request returned by the add operation
 * @param {*} serie the serie of the equipment the part was added to
 */
export const resolveSubstitutionOutcome = (requestedCode, anticipatedSubstitute, resultingRequest, serie) => {
	let report = readSubstitutionReport(resultingRequest)
	if (!report) return resolveByComparison(requestedCode, anticipatedSubstitute, resultingRequest, serie)

	// Already present is reported on top of a substitution, so it is read first: the user needs to
	// know nothing was added before they need to know what it would have been replaced by.
	if (report.WasAlreadyPresent === true) return SubstitutionOutcome.ALREADY_PRESENT
	if (report.WasSubstituted === true) return SubstitutionOutcome.SUBSTITUTED
	return SubstitutionOutcome.NONE
}

/**
 * Message shown when the substitute is already in the request, so nothing was added.
 * @param {*} requestedCode the spare part code the user picked
 * @param {*} substitute the substitute material, as `{ Code, Description }`
 */
export const buildAlreadyPresentMessage = (requestedCode, substitute) => {
	let substituteLabel = buildMaterialLabel(substitute)
	if (substituteLabel.length === 0) {
		return `Spare part ${requestedCode} is discontinued and its substitute material is already in this request.`
	}
	return `Spare part ${requestedCode} is discontinued. Its substitute ${substituteLabel} is already in this request.`
}

/**
 * Build the message shown after an add that involved a discontinued spare part.
 *
 * Returns null when there is nothing worth telling the user, so callers can treat "no message" as
 * the normal case instead of special-casing empty strings.
 *
 * When the applied material's description is unavailable, the message degrades to naming the code.
 * It never claims a substitution it could not confirm.
 *
 * @param {*} outcome one of SubstitutionOutcome
 * @param {*} requestedCode the spare part code the user picked
 * @param {*} appliedMaterial the material that went in, as `{ Code, Description }`, or null
 */
export const buildSubstitutionMessage = (outcome, requestedCode, appliedMaterial) => {
	if (outcome === SubstitutionOutcome.NONE) return null

	if (outcome === SubstitutionOutcome.ALREADY_PRESENT) return buildAlreadyPresentMessage(requestedCode, appliedMaterial)

	if (outcome === SubstitutionOutcome.SUBSTITUTED) {
		let substituteLabel = buildMaterialLabel(appliedMaterial)
		if (substituteLabel.length === 0) {
			return `Spare part ${requestedCode} is discontinued and was replaced by its substitute material.`
		}
		return `Spare part ${requestedCode} is discontinued. It was replaced by ${substituteLabel}.`
	}

	return `Spare part ${requestedCode} is discontinued. Review your request to confirm which material was added.`
}

/**
 * The whole feedback decision for one add, as the message to show or null.
 *
 * Single entry point for the callers so the outcome and the material the message names are always
 * read from the same source — the server's report when it is there, the anticipated substitute
 * only as a fallback.
 *
 * @param {*} requestedCode the spare part code the user picked
 * @param {*} anticipatedSubstitute the substitute the catalog predicted, or null
 * @param {*} resultingRequest the request returned by the add operation
 * @param {*} serie the serie of the equipment the part was added to
 */
export const buildAddFeedbackMessage = (requestedCode, anticipatedSubstitute, resultingRequest, serie) => {
	let outcome = resolveSubstitutionOutcome(requestedCode, anticipatedSubstitute, resultingRequest, serie)
	if (outcome === SubstitutionOutcome.NONE) return null

	// Nothing was anticipated and nothing was reported: no discontinued material is in play, so the
	// vague "review your request" line would be noise on an ordinary add.
	if (outcome === SubstitutionOutcome.UNDETERMINED && !anticipatedSubstitute && !readSubstitutionReport(resultingRequest)) return null

	return buildSubstitutionMessage(outcome, requestedCode, getAppliedMaterial(resultingRequest, anticipatedSubstitute))
}

/**
 * Whether the substitute of a discontinued spare part is already in the request FOR THIS EQUIPMENT.
 *
 * Scoped to the serie because that is how the request identifies a row: the same replacement part
 * needed by two different coolers is two legitimate rows. Checking request-wide would refuse the
 * second equipment's part and report it as already present, while the server would have accepted it.
 *
 * This runs BEFORE the call, to spare a round trip the server would answer with the same verdict.
 * It does not replace the server's report, which is what the outcome is read from afterwards.
 *
 * @param {*} substitute the substitute material recorded in the catalog
 * @param {*} codesInRequest map keyed by serie and normalized code
 * @param {*} serie the serie of the equipment the part is being added to
 */
export const isSubstituteAlreadyInRequest = (substitute, codesInRequest, serie) => {
	if (!substitute || !codesInRequest) return false
	let key = normalizeMaterialCode(substitute.Code)
	if (key.length === 0) return false
	return !!codesInRequest[sparePartCodeKey(serie, key)]
}

/**
 * The notice shown after an Excel import into a Spare Part Sales request, or null when the server
 * substituted nothing.
 *
 * One line per discontinued part the server replaced, in the order it reported them, naming the
 * requested code and the material that went in. A part whose substitute was already in the request
 * is still listed — the user asked for it and needs to know why no row was added — but marked as such.
 * Parts that went in as requested are left out: the summary is about substitutions, not the import.
 *
 * @param {*} substitutions the reports the import returned, one per substituted part
 */
export const buildImportSubstitutionSummary = (substitutions) => {
	if (!Array.isArray(substitutions)) return null

	// The server lists only rows the catalog substituted. An already-present one is kept even if it
	// does not also state WasSubstituted, so the user still learns why that row was skipped.
	let replaced = substitutions.filter((report) => report && (report.WasSubstituted === true || report.WasAlreadyPresent === true))
	if (replaced.length === 0) return null

	let lines = replaced.map((report) => {
		let substituteLabel = buildMaterialLabel({ Code: report.AppliedCode, Description: report.AppliedDescription })
		let line = `${report.RequestedCode} → ${substituteLabel.length > 0 ? substituteLabel : 'its substitute material'}`
		return report.WasAlreadyPresent === true ? `${line} (already in this request)` : line
	})

	let header =
		replaced.length === 1
			? '1 discontinued spare part was replaced by its substitute material:'
			: `${replaced.length} discontinued spare parts were replaced by their substitute material:`
	return [header, ...lines].join('\n')
}
